import { ArrowBackIcon, ArrowForwardIcon, CheckIcon, ChevronDownIcon } from '@chakra-ui/icons'
import {
  Box,
  Center,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from '@chakra-ui/react'
import { colors } from '@hb/shared'
import { useMemo } from 'react'
import { useGynCareView } from './context'
import { StageProgress } from './StageProgress'
import { GYNStage } from './types'

const MobileGynStageButton = ({
  stage,
  complete,
  withChevron,
}: {
  stage: GYNStage
  complete: boolean
  withChevron?: boolean
}) => (
  <Flex py={2} px={4} w="100%" bg="white" align="center" borderRadius={6} gap={2}>
    {withChevron ? <CheckIcon w={5} h={5} color={complete ? colors.green.hex : '#999999'} /> : null}
    <Text textAlign="left" flex={1} minW={0} fontFamily="Hero-New">
      {stage.name}
    </Text>
    {withChevron ? null : <CheckIcon w={5} h={5} color={complete ? colors.green.hex : '#999999'} />}
    {withChevron ? <ChevronDownIcon ml="auto" w={6} h={6} /> : null}
  </Flex>
)

export const MobileGynStageList = () => {
  const { stages, stageIndex: activeIndex, handleStageSelect } = useGynCareView()
  const activeStage = useMemo(() => stages[activeIndex], [activeIndex, stages])
  const prevStageIndex = useMemo(() => (activeIndex > 0 ? activeIndex - 1 : null), [activeIndex])
  const nextStageIndex = useMemo(
    () => (activeIndex < stages.length - 1 ? activeIndex + 1 : null),
    [activeIndex, stages],
  )

  return (
    <Flex w="100%" flexFlow="column" gap={2}>
      <Flex p={2} gap={1} justifyContent="center" align="center" w="100%">
        <Center
          pointerEvents={prevStageIndex !== null ? 'auto' : 'none'}
          opacity={prevStageIndex !== null ? 1 : 0}
          width={prevStageIndex !== null ? 10 : 0}
          height={10}
          transition="opacity 300ms, width 300ms">
          <IconButton
            boxShadow="0 0 3px #00000055"
            onClick={() => handleStageSelect(prevStageIndex ?? 0)}
            size="sm"
            bg="white"
            borderRadius="full"
            icon={<ArrowBackIcon w={5} h={5} />}
            aria-label="Previous Stage"
          />
        </Center>
        <Box flex={1}>
          <Menu matchWidth>
            <MenuButton boxShadow="1px 1px 5px #00000055" borderRadius={6} w="100%">
              <MobileGynStageButton withChevron stage={activeStage} complete={true} />
            </MenuButton>
            <MenuList gap={0} p={0} boxShadow="1px 1px 5px #00000055" border="none">
              {stages.map((s, i) => (
                <MenuItem
                  px={0}
                  bg="transparent"
                  key={s.name}
                  onClick={() => handleStageSelect(i)}
                  borderTop={i ? '1px solid #cdcdcd' : 'none'}
                  _focus={{ bg: 'transparent' }}
                  _hover={{ bg: 'transparent' }}>
                  <MobileGynStageButton stage={s} complete={false} />
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </Box>
        <Center
          pointerEvents={nextStageIndex !== null ? 'auto' : 'none'}
          opacity={nextStageIndex !== null ? 1 : 0}
          width={nextStageIndex !== null ? 10 : 0}
          transition="opacity 300ms, width 300ms"
          height={10}>
          <IconButton
            boxShadow="0 0 3px #00000055"
            size="sm"
            onClick={() => handleStageSelect(nextStageIndex ?? stages.length - 1)}
            bg="white"
            borderRadius="full"
            icon={<ArrowForwardIcon w={5} h={5} />}
            aria-label="Previous Stage"
          />
        </Center>
      </Flex>
      <StageProgress />
    </Flex>
  )
}
